import { SubscriptionsTabsStatus } from '@blockpulse3/data/shared';
import {
  FundraisingInfoFragment,
  IdentityType,
  SubscriptionFilterType,
  SubscriptionStepStatus,
  SubscriptionStepType,
  SubscriptionsCountInfosFragment,
  SubscriptionsFilterInput,
} from '@blockpulse3/graphql/hooks';

export type TabsStatus = {
  name: SubscriptionsTabsStatus;
  count: number;
};

/* ** STEP Filters ** */
export const subscriptionFilterStep: SubscriptionsFilterInput[] = [
  {
    type: SubscriptionFilterType.STEP,
    name: 'MifidInProgress',
    value: 'MifidInProgress',
    stepType: SubscriptionStepType.MIFID,
    stepStatuses: [SubscriptionStepStatus.IN_PROGRESS],
  },
  {
    type: SubscriptionFilterType.STEP,
    name: 'KycKybMissing',
    value: 'KycKybMissing',
    stepType: SubscriptionStepType.IDENTITY_VERIFICATION,
    stepStatuses: [SubscriptionStepStatus.NONE, SubscriptionStepStatus.IN_PROGRESS],
  },
  {
    type: SubscriptionFilterType.STEP,
    name: 'KycKybPending',
    value: 'KycKybPending',
    stepType: SubscriptionStepType.IDENTITY_VERIFICATION,
    stepStatuses: [SubscriptionStepStatus.PENDING],
  },
  {
    type: SubscriptionFilterType.STEP,
    name: 'KycKybRefused',
    value: 'KycKybRefused',
    stepType: SubscriptionStepType.IDENTITY_VERIFICATION,
    stepStatuses: [SubscriptionStepStatus.ERROR],
  },
  {
    type: SubscriptionFilterType.STEP,
    name: 'AmountNotChosed',
    value: 'AmountNotChosed',
    stepType: SubscriptionStepType.DETAILS,
    stepStatuses: [SubscriptionStepStatus.NONE, SubscriptionStepStatus.ERROR],
  },
  {
    type: SubscriptionFilterType.STEP,
    name: 'SignatureNotSigned',
    value: 'SignatureNotSigned',
    stepType: SubscriptionStepType.DOCUMENT_SIGNATURE,
    stepStatuses: [SubscriptionStepStatus.NONE, SubscriptionStepStatus.ERROR],
  },
  {
    type: SubscriptionFilterType.STEP,
    name: 'SignatureInProgress',
    value: 'SignatureInProgress',
    stepType: SubscriptionStepType.DOCUMENT_SIGNATURE,
    stepStatuses: [SubscriptionStepStatus.PENDING, SubscriptionStepStatus.IN_PROGRESS],
  },
  {
    type: SubscriptionFilterType.STEP,
    name: 'PaymentInProgress',
    value: 'PaymentInProgress',
    stepType: SubscriptionStepType.PAYMENT,
    stepStatuses: [
      SubscriptionStepStatus.NONE,
      SubscriptionStepStatus.PENDING,
      SubscriptionStepStatus.IN_PROGRESS,
      SubscriptionStepStatus.ERROR,
    ],
  },
];

/* ** IDENTITY_TYPE Filters ** */
export const subscriptionFilterIdentity: SubscriptionsFilterInput[] = [
  {
    type: SubscriptionFilterType.IDENTITY_TYPE,
    name: 'IndividualIdentity',
    value: IdentityType.INDIVIDUAL,
  },
  {
    type: SubscriptionFilterType.IDENTITY_TYPE,
    name: 'CompanyIdentity',
    value: IdentityType.COMPANY,
  },
];

/**
 * getSubscriptionFilterStep.
 *
 * @param {FundraisingInfoFragment} fundraising
 * @returns {SubscriptionsFilterInput[]}
 */
export function getSubscriptionFilterStep(
  fundraising?: FundraisingInfoFragment,
): SubscriptionsFilterInput[] {
  if (!fundraising || !fundraising?.subscriptionSteps) {
    return subscriptionFilterStep;
  }

  let subscriptionFilterStepPartial = subscriptionFilterStep;

  const detailsStep = fundraising.subscriptionSteps.some(
    (step) => step.__typename === 'SubscriptionDetailsStepType',
  );
  if (!detailsStep) {
    subscriptionFilterStepPartial = subscriptionFilterStepPartial.filter(
      (filter) => filter.stepType !== SubscriptionStepType.DETAILS,
    );
  }

  const mifidStep = fundraising.subscriptionSteps.some(
    (step) => step.__typename === 'MifidStepType',
  );
  if (!mifidStep) {
    subscriptionFilterStepPartial = subscriptionFilterStepPartial.filter(
      (filter) => filter.stepType !== SubscriptionStepType.MIFID,
    );
  }

  return subscriptionFilterStepPartial;
}

/**
 * getSubscriptionFilterHoldingMethod.
 *
 * @param {FundraisingInfoFragment} fundraising
 * @returns {SubscriptionsFilterInput[]}
 */
export function getSubscriptionFilterHoldingMethod(
  fundraising?: FundraisingInfoFragment,
): SubscriptionsFilterInput[] {
  if (!fundraising || !fundraising?.allowedHoldingMethods) {
    return [];
  }

  return fundraising.allowedHoldingMethods.map((method) => ({
    type: SubscriptionFilterType.HOLDING_METHOD,
    name: method,
    value: method,
  }));
}

export function getSubscriptionFilterTags(
  fundraising?: FundraisingInfoFragment,
): SubscriptionsFilterInput[] {
  if (!fundraising || !fundraising?.company?.tags) {
    return [];
  }

  return fundraising.company.tags.map((tag) => ({
    type: SubscriptionFilterType.TAGS,
    name: tag.name,
    value: tag.id,
  }));
}

export function getTabsWithCount(
  count?: SubscriptionsCountInfosFragment,
  hasMifid = false,
  isAllIn = false,
): TabsStatus[] {
  return [
    {
      name: SubscriptionsTabsStatus.ALL_STATUS,
      count: count?.total || 0,
    },
    {
      name: SubscriptionsTabsStatus.NOT_SIGNED_UP,
      count: count?.invitationSent || 0,
    },
    {
      name: SubscriptionsTabsStatus.SIGNED_UP,
      count: count?.signedUp || 0,
    },
    {
      name: SubscriptionsTabsStatus.PENDING,
      count: count?.pending || 0,
    },
    hasMifid
      ? {
          name: SubscriptionsTabsStatus.COMPLETED,
          count: count?.completed || 0,
        }
      : undefined,
    {
      name: SubscriptionsTabsStatus.APPROVED,
      count: count?.approved || 0,
    },
    isAllIn
      ? {
          name: SubscriptionsTabsStatus.FINALIZED,
          count: count?.finalized || 0,
        }
      : undefined,
    {
      name: SubscriptionsTabsStatus.REFUSED,
      count: count?.refused || 0,
    },
  ].filter((tab) => tab) as TabsStatus[];
}
